"use client";

/**
 * KeywordFilter — 검색어 필터 (검색 유형 선택 + 키워드 입력)
 *
 * 거의 모든 관리자 필터에 들어가는 "검색어" 영역을 공통화한 것.
 * AdminFilterField(라벨 좌 / 컨트롤 우)를 품고 있어 AdminFilter 안에 바로 넣으면 된다.
 * 제어 컴포넌트이며, 비즈니스 로직 없이 값/콜백만 받는다.
 *
 * @example
 * <KeywordFilter
 *   typeOptions={[{ value: "name", label: "이름" }, { value: "email", label: "이메일" }]}
 *   type={filters.keywordType}
 *   onTypeChange={(v) => setField("keywordType", v)}
 *   keyword={filters.keyword}
 *   onKeywordChange={(v) => setField("keyword", v)}
 * />
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Input } from "../ui/input";
import { AdminFilterField } from "./admin-filter";

export interface KeywordTypeOption {
  value: string;
  label: string;
}

export interface KeywordFilterProps {
  /** 필드 라벨 (기본 "검색어") */
  label?: ReactNode;
  /**
   * 검색 유형 선택 옵션. 주면 키워드 입력 왼쪽에 select 가 표시된다.
   * (예: 이름/이메일/전화번호 중 무엇으로 검색할지)
   */
  typeOptions?: KeywordTypeOption[];
  /** 선택된 검색 유형 값 */
  type?: string;
  onTypeChange?: (value: string) => void;
  /** 키워드 값 */
  keyword: string;
  onKeywordChange: (value: string) => void;
  /** 입력창 placeholder (기본 "검색어 입력") */
  placeholder?: string;
  /** 검색 유형 select 의 "전체" 옵션 라벨. null 이면 표시 안 함 (기본 "전체") */
  allTypeLabel?: string | null;
  htmlFor?: string;
  className?: string;
}

const selectClass =
  "h-9 shrink-0 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

export function KeywordFilter({
  label = "검색어",
  typeOptions,
  type,
  onTypeChange,
  keyword,
  onKeywordChange,
  placeholder = "검색어 입력",
  allTypeLabel = "전체",
  htmlFor = "keyword",
  className,
}: KeywordFilterProps) {
  return (
    <AdminFilterField label={label} htmlFor={htmlFor} className={className}>
      <div className="flex gap-2">
        {typeOptions && typeOptions.length > 0 && (
          <select
            aria-label="검색 유형"
            value={type ?? ""}
            onChange={(e) => onTypeChange?.(e.target.value)}
            className={cn(selectClass, "w-28")}
          >
            {allTypeLabel !== null && <option value="">{allTypeLabel}</option>}
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        <Input
          id={htmlFor}
          placeholder={placeholder}
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
        />
      </div>
    </AdminFilterField>
  );
}
